import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PageTitleService } from '../../core/services/page-title.service';
import { MachineMaintenanceService } from '../../core/services/machine-maintenance.service';

interface TableItem {
  trClass: string;
  tdClass: string;
  labelIndex?: number;
  value: string;
  url?: string;
  colspan?: number;
}

interface VideoItem {
  title: string;
  embedUrl: SafeResourceUrl | null;
}

const TR_CLASS: Record<number, string> = {
  1: 'daily-maintenance',
  2: 'normal-maintenance',
  3: 'maintenance-tools',
};
const TD_CLASS: Record<number, string> = {
  1: 'title-1',
  2: 'title-2',
  3: 'title-3',
};
const TITLE_COLSPAN: Record<number, number> = {
  1: 5,
  2: 4,
  3: 3,
};

@Component({
    selector: 'app-machine-maintenance',
    imports: [],
    template: `
<div class="animated fadeIn">
  <div class="row sfapp-container">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <span><i class="fa s-icon-list"></i> 店舗メンテナンス動画</span>
        </div>
        <div class="card-body" style="align-self: center;">
          @if (loading) {
            <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
          }
          @if (!loading && items.length === 0) {
            <span>該当するデータはありません</span>
          }
          @if (!loading && items.length > 0) {
            <table class="table table-borderless table-sm table-hover machine-maintenance-table" style="max-width: 1200px">
              <thead>
                <tr>
                  <th width="5%"></th>
                  <th width="5%"></th>
                  <th width="5%"></th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                @for (item of items; track $index) {
                  <tr class="{{ item.trClass }}">
                    @if (!item.labelIndex) {
                      <td class="non-clickable"></td>
                    }
                    @if (!item.labelIndex || item.labelIndex === 3) {
                      <td class="non-clickable"></td>
                    }
                    @if (item.labelIndex !== 1) {
                      <td class="non-clickable"></td>
                    }
                    <td class="{{ item.tdClass }}"
                        [attr.colspan]="item.colspan || null"
                        [attr.tabindex]="!item.labelIndex && item.url ? 0 : null"
                        (keydown.enter)="openVideo(item)" (keydown.space)="$event.preventDefault(); openVideo(item)"
                        (click)="openVideo(item)">@if (!item.labelIndex && item.url !== '') {<i class="fa fa-play-circle" aria-hidden="true"></i>}&nbsp;{{ item.value }}</td>
                    @if (item.url !== undefined) {
                      <td>
                        <a href="{{ item.url }}" target="_blank">{{ item.url }}&nbsp;@if (item.url !== '') {<i class="fa fa-external-link" aria-hidden="true"></i>}</a>
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Video Modal -->
@if (showModal && currentVideo) {
  <div class="modal-backdrop fade show" (click)="closeModal()"></div>
  <div class="modal fade show d-block" id="movieModal" tabindex="-1" role="dialog" aria-labelledby="movieModalLabel">
    <div class="modal-dialog machine-maintenance-video-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="movieModalLabel">
            @if (currentVideo.embedUrl) {
              <i class="fa fa-play-circle" aria-hidden="true"></i>
            }
            &nbsp;{{ currentVideo.title }}
          </h5>
        </div>
        <div class="modal-body machine-maintenance-video-body">
          @if (currentVideo.embedUrl) {
            <div id="iframeContainer" class="form-group row m-0 machine-maintenance-video-frame">
              <iframe [src]="currentVideo.embedUrl"
                      title="YouTube video player"
                      frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerpolicy="strict-origin-when-cross-origin"
                      allowfullscreen>
              </iframe>
            </div>
          }
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="closeModal()">
            <i class="fa s-icon-cancel fa-fw"></i>&nbsp;閉じる
          </button>
        </div>
      </div>
    </div>
  </div>
}
  `,
    styles: [`
    .machine-maintenance-video-dialog {
      width: calc(100% - 34px);
      max-width: 650px;
      margin: 1.75rem auto;
    }

    .machine-maintenance-video-body {
      padding: 0;
    }

    .machine-maintenance-video-frame {
      position: relative;
      width: 100%;
      aspect-ratio: 16 / 9;
      background: #000;
      overflow: hidden;
    }

    .machine-maintenance-video-frame iframe {
      position: absolute;
      inset: 0;
      display: block;
      width: 100%;
      height: 100%;
      border: 0;
    }

    @media (max-width: 575.98px) {
      .machine-maintenance-video-dialog {
        width: calc(100% - 16px);
        margin: 0.5rem auto;
      }
    }
  `]
})
export class MachineMaintenanceComponent implements OnInit, OnDestroy {
  private pageTitleService = inject(PageTitleService);
  private machineMaintenanceService = inject(MachineMaintenanceService);
  private sanitizer = inject(DomSanitizer);

  items: TableItem[] = [];
  loading = true;
  showModal = false;
  currentVideo: VideoItem | null = null;

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('マシンメンテナンス', 'fa fa-wrench');
    this.loadChatWidget();
    try {
      const data: any[] = await this.machineMaintenanceService.get() ?? [];
      this.buildItems(data);
    } finally {
      this.loading = false;
    }
  }

  private loadChatWidget(): void {
    const existingChat = document.getElementsByTagName('chat')[0];
    if (existingChat) {
      existingChat.classList.remove('d-none');
      return;
    }
    if (document.querySelector('script[src*="chatplus.jp"]')) return;
    const d = document as any;
    d['__cp_d'] = 'https://app.chatplus.jp';
    d['__cp_c'] = '5b46b89e_3';
    const a = document.createElement('script');
    a.async = true;
    a.src = 'https://app.chatplus.jp/cp.js';
    const m = document.getElementsByTagName('script')[0];
    m.parentNode!.insertBefore(a, m);
  }

  ngOnDestroy(): void {
    document.getElementsByTagName('chat')[0]?.classList.add('d-none');
  }

  private buildItems(data: any[]): void {
    let beforeLabel1 = '';
    let beforeLabel2 = '';
    let beforeLabel3 = '';

    data.forEach((d, i) => {
      if (d.label1 !== beforeLabel1) {
        this.addItem(d.label1, undefined, TR_CLASS[d.id], 1);
      }
      if (d.label2 !== beforeLabel2) {
        this.addItem(d.label2, undefined, TR_CLASS[d.id], 2);
      }
      if (d.label3 !== beforeLabel3) {
        this.addItem(d.label3, undefined, TR_CLASS[d.id], 3);
      }
      this.addItem(d.value, d.url, TR_CLASS[d.id]);

      if (data.length === i + 1 || data[i + 1].label3 !== d.label3) {
        this.addItem('', '', TR_CLASS[d.id]);
      }

      beforeLabel1 = d.label1;
      beforeLabel2 = d.label2;
      beforeLabel3 = d.label3;
    });
  }

  private addItem(value: string, url: string | undefined, trClass: string, labelIndex?: number): void {
    let tdClass = labelIndex ? TD_CLASS[labelIndex] : '';
    if (!url) {
      tdClass = (tdClass + ' non-clickable').trim();
    }
    if (labelIndex === 2 && !value) {
      tdClass = (tdClass + ' non-border-top').trim();
    }
    const colspan = labelIndex ? TITLE_COLSPAN[labelIndex] : undefined;
    this.items.push({ trClass, tdClass, labelIndex, value, url, colspan });
  }

  openVideo(item: TableItem): void {
    if (!item.labelIndex && item.url) {
      const raw = item.url.replace('youtu.be', 'www.youtube.com/embed') + '?autoplay=1&mute=1';
      const embedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(raw);
      this.currentVideo = { title: item.value, embedUrl };
      this.showModal = true;
    } else if (!item.labelIndex) {
      this.currentVideo = { title: item.value, embedUrl: null };
      this.showModal = true;
    }
  }

  closeModal(): void {
    this.showModal = false;
    this.currentVideo = null;
  }
}
